import React, { useState, useEffect } from 'react';
import {
  Button,
  IconButton,
  TextField as TextFieldBase,
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Typography } from '../../../../../components/Generic/StyledComponent';
import Modal from 'src/components/common/modal';
import { Formik, Field } from 'formik';
import * as yup from 'yup';
import LoaderButton from '../loaderButton';
import { MutualAid } from 'src/services/api';
import useCredentials from 'src/hooks/useCredentials';
import { USER_ROLES } from 'src/constants';
import { newHasRole } from '../..';

const FIELDS = {
  COMMENTS: 'comments',
  REJECTIONCATEGORY: 'rejectionCategory',
  CUSTOM_REASON: 'customReason'
};

const VALIDATION_SCHEMA = yup.object({
  [FIELDS.REJECTIONCATEGORY]: yup.string().required('Required'),
  [FIELDS.COMMENTS]: yup.string().required('Required'),
  [FIELDS.CUSTOM_REASON]: yup.string().when(FIELDS.COMMENTS, {
    is: 'Others',
    then: (schema) =>
      schema
        .required('Required')
        .min(15, 'Minimum 15 characters required')
        .max(500, 'Maximum 500 characters allowed'),
    otherwise: (schema) => schema.notRequired()
  })
});

function ActionModal({
  headerLabel,
  open,
  handleClose,
  onCancelPress,
  onSuccessPress,
  apiLoading
}) {
  const successBtnColor =
    headerLabel === 'Approve' ? 'rgba(76, 175, 80, 1)' : '#f44336';
  const { accessToken } = useCredentials();

  const [categories, setCategories] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  const getReviewerRole = () => {
    if (newHasRole([USER_ROLES.IO_Reviewer])) {
      return USER_ROLES.IO_Reviewer;
    } else if (newHasRole([USER_ROLES.DOC_Reviewer])) {
      return USER_ROLES.DOC_Reviewer;
    } else if (newHasRole([USER_ROLES.FO_Reviewer])) {
      return USER_ROLES.FO_Reviewer;
    } else if (newHasRole([USER_ROLES.Claim_Manager_Reviewer])) {
      return USER_ROLES.Claim_Manager_Reviewer;
    } else if (newHasRole([USER_ROLES.Higher_Level_Reviewer])) {
      return USER_ROLES.Higher_Level_Reviewer;
    }
  };

  const fetchCategories = async (params = {}) => {
    setLoadingCategories(true);
    try {
      const reviewer = getReviewerRole();
      const data = {
        status: 'REJECT',
        page: params.nextPage || 0,
        size: 50,
        searchTerm: params.search || ''
      };

      const response = await MutualAid.getNonRegisterCategory({
        accessToken,
        reviewer,
        data
      });

      if (response.code === 200) {
        const transformedData = (response?.data || []).map((item) => ({
          id: item?.id || item,
          name: item?.name || item
        }));
        setCategories(transformedData);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchReasons = async (params = {}) => {
    if (!params.categoryId) {
      setReasons([]);
      return;
    }

    setLoadingReasons(true);
    try {
      const reviewer = getReviewerRole();
      const data = {
        status: 'REJECT',
        page: params.nextPage || 0,
        size: 50,
        categoryId: params.categoryId
      };

      const response = await MutualAid.getNonRegisterCategoryReason({
        accessToken,
        reviewer,
        data
      });

      if (response.code === 200) {
        const transformedData = (response?.data || []).map((item) => ({
          id: item,
          name: item
        }));
        setReasons(transformedData);
      }
    } catch (error) {
      console.error('Error fetching reasons:', error);
      setReasons([]);
    } finally {
      setLoadingReasons(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCategories();
      setSelectedCategoryId('');
      setReasons([]);
    }
  }, [open]);

  useEffect(() => {
    if (selectedCategoryId) {
      fetchReasons({ categoryId: selectedCategoryId });
    } else {
      setReasons([]);
    }
  }, [selectedCategoryId]);

  const handleCategoryChange = (name, setFieldValue) => {
    const selectedCategory = categories.find((item) => item.name == name);
    setSelectedCategoryId(selectedCategory?.id || '');
    setFieldValue(FIELDS.REJECTIONCATEGORY, name);
    setFieldValue(FIELDS.COMMENTS, '');
    setFieldValue(FIELDS.CUSTOM_REASON, '');
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      withoutScroll
      childrenStyle={{ padding: 24 }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          marginBottom: 20
        }}
      >
        <Typography>{headerLabel}</Typography>
        <IconButton style={{ padding: 5 }} onClick={handleClose} size="large">
          <CloseIcon />
        </IconButton>
      </div>
      <Formik
        initialValues={{
          [FIELDS.COMMENTS]: '',
          [FIELDS.REJECTIONCATEGORY]: '',
          [FIELDS.CUSTOM_REASON]: ''
        }}
        onSubmit={(values) => {
          const payload = {
            rejectionCategory: values.rejectionCategory,
            comments:
              values.comments === 'Others'
                ? values.customReason
                : values.comments
          };
          if (values.comments === 'Others') {
            payload.categoryId = selectedCategoryId;
          }

          onSuccessPress(payload);
        }}
        validationSchema={VALIDATION_SCHEMA}
      >
        {({ handleSubmit, setFieldValue, values }) => (
          <>
            <Field
              name={FIELDS.REJECTIONCATEGORY}
              component={({ field, form }) => (
                <SelectField
                  field={field}
                  form={form}
                  label="Rejection Category"
                  options={categories}
                  loading={loadingCategories}
                  onChange={(value) =>
                    handleCategoryChange(value, setFieldValue)
                  }
                />
              )}
            />
            <Divider
              style={{ width: '100%', marginBottom: 24, borderStyle: 'dashed' }}
            />
            <Field
              name={FIELDS.COMMENTS}
              component={({ field, form }) => (
                <SelectField
                  field={field}
                  form={form}
                  label="Reason for Reject"
                  options={reasons}
                  loading={loadingReasons}
                  disabled={!selectedCategoryId}
                  onChange={(value) => {
                    form.setFieldValue(FIELDS.COMMENTS, value);
                    if (value !== 'Others') {
                      form.setFieldValue(FIELDS.CUSTOM_REASON, '');
                    }
                  }}
                />
              )}
            />

            {values.comments === 'Others' && (
              <>
                <Divider
                  style={{
                    width: '100%',
                    marginBottom: 24,
                    borderStyle: 'dashed'
                  }}
                />{' '}
                <Field
                  name={FIELDS.CUSTOM_REASON}
                  component={TextField}
                  label="Remarks"
                />
              </>
            )}

            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end'
              }}
            >
              <Button
                variant="outlined"
                style={{
                  marginRight: 10,
                  textTransform: 'capitalize'
                }}
                onClick={onCancelPress || handleClose}
              >
                Cancel
              </Button>
              <LoaderButton
                variant="contained"
                style={{
                  textTransform: 'capitalize',
                  backgroundColor: successBtnColor
                }}
                onClick={handleSubmit}
                loading={apiLoading}
              >
                {headerLabel} Reimbursement
              </LoaderButton>
            </div>
          </>
        )}
      </Formik>
    </Modal>
  );
}

export default ActionModal;

function TextField({
  field: { onChange, value, name },
  form: { errors },
  label
}) {
  return (
    <>
      <TextFieldBase
        name={name}
        label={`${label}`}
        variant="outlined"
        style={{ width: '100%' }}
        value={value}
        onChange={onChange}
        error={!!errors[name]}
        multiline
        rows={3}
      />
      <FormHelperText error>{errors[name]}</FormHelperText>
    </>
  );
}

function SelectField({
  field: { onChange, value, name },
  form: { errors, touched },
  options = [],
  label,
  loading = false,
  disabled = false,
  onChange: customOnChange
}) {
  const handleChange = (event) => {
    if (customOnChange) {
      customOnChange(event.target.value);
    }
    onChange(event);
  };
  return (
    <>
      <FormControl
        variant="outlined"
        fullWidth
        disabled={disabled}
        error={touched?.[name] && Boolean(errors?.[name])}
      >
        <InputLabel>{label}</InputLabel>
        <Select
          name={name}
          value={value}
          onChange={handleChange}
          label={label}
          MenuProps={{
            PaperProps: {
              className: 'custom-select-menu',
              style: {
                maxWidth: '100%',
                whiteSpace: 'normal',
                maxHeight: 300,
                overflowY: 'auto'
              }
            }
          }}
        >
          <MenuItem value="">
            <em>Select {label}</em>
          </MenuItem>
          {loading ? (
            <MenuItem disabled>Loading...</MenuItem>
          ) : (
            options.map((item) => (
              <MenuItem
                key={item?.id || item}
                value={item?.name || item}
                style={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word'
                }}
              >
                {item?.name || item}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
      <FormHelperText error>{errors[name]}</FormHelperText>
    </>
  );
}
